import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import {
  listServices,
  deleteService,
  updateServiceStatus,
} from "../../../../actions/serviceActions";
import { useNavigate, useParams } from "react-router-dom";
import Table from "../../../../components/shared/Table";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import ToastComponent from "../../../../components/partials/ToastComponent";
import { columnsDesktop } from "../columns/ServiceListColumns"; // Adjust the path according to your file structure
import { columnsMobile } from "../columns/ServiceListColumnsMobile"; // Adjust the path according to your file structure
import { useTranslation } from "react-i18next";
import {
  SERVICE_DELETE_RESET,
  SERVICE_UPDATE_RESET,
  SERVICE_CREATE_RESET,
} from "../../../../constants/serviceConstants";
import SidePanel from "../../../../components/sidepanelNew/SidePanel";
import CreateService from "../sidepanels/CreateService";
import EditService from "../sidepanels/EditService";
import { SERVICE_UPDATE_STATUS_RESET } from "../../../../constants/serviceConstants";
// import useRoleCheck from "../../../../hooks/useRoleCheck";
import { primary } from "../../../../utils/colors";

import useThemeColors from "../../../../hooks/useThemeColors";
import PageHeader from "../../../../components/layout/PageHeader";
import SearchInput from "../../../../components/input/SearchInput";
import TableContainerWindowed from "../../../../components/shared/Table/TableContainerWindowed";
import useDebounce from "../../../../hooks/useDebounce";
import ConfirmDeleteModal from "../../../../components/modals/ConfirmDeleteModal";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import UserNoAccess from "../../../../components/partials/UserNoAccess";
import useIsMobile from "../../../../hooks/useIsMobile";
import DrawerNew from "../../../../components/menu/DrawerNew";

const ServiceListNew = () => {
  // GENERAL
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastComponent = ToastComponent();
  const { t } = useTranslation();
  const translate = (key) => t(key);
  const isMobile = useIsMobile();

  // STATE
  const [isInPlans, setIsInPlans] = useState(false);

  // FETCHING LOADER
  const [isFetching, setIsFetching] = useState(false);

  // COLORS
  const { whiteMode } = useThemeColors();

  // ROLES
  // const isDoctor = useRoleCheck("employee_doctor");
  // const isAdmin = useRoleCheck("admin");

  // const [selectedGroup, setSelectedGroup] = useState("");

  // PAGINATION
  const [page, setPage] = useState(1 || 1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizes = [4, 10, 25, 100];

  // SEARCH
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 600); // Use debounced value
  const normalizeText = (text) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };
  const onChangeSearchTitle = (e) => {
    const normalizedText = normalizeText(e.target.value);
    setSearchTerm(normalizedText);
    setPage(1);
  };

  // SIDEPANELS
  const [sidePanelModalOpen, setSidePanelOpen] = useState(false);
  const [editSidePanelOpen, setEditSidePanelOpen] = useState(false);

  // STATE
  const [serviceId, setServiceId] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [serviceName, setServiceName] = useState("");

  // REDUX
  const authLogin = useSelector((state) => state.authLogin);
  const { userInfo } = authLogin;

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  const serviceList = useSelector((state) => state.serviceList);
  const {
    loading,
    error,
    services,
    pages,
    totalCount,
    countsByStatus,
    totalAveragePrice,
    totalAverageDuration,
    globalTotalAveragePrice,
    globalTotalAverageDuration,
  } = serviceList;

  const serviceCreate = useSelector((state) => state.serviceCreate);
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    service: createdService,
  } = serviceCreate;

  const serviceUpdate = useSelector((state) => state.serviceUpdate);
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = serviceUpdate;

  const serviceDelete = useSelector((state) => state.serviceDelete);

  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = serviceDelete;

  const serviceGroupList = useSelector((state) => state.serviceGroupList);
  const { serviceGroups } = serviceGroupList;

  const serviceUpdateStatus = useSelector((state) => state.serviceUpdateStatus);
  const {
    loading: loadingUpdateStatus,
    error: errorUpdateStatus,
    success: successUpdateStatus,
  } = serviceUpdateStatus;

  // SORTING
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSortChange = (column) => {
    const newSortOrder =
      sortField === column && sortOrder === "asc" ? "desc" : "asc";

    if (sortField !== column || sortOrder !== newSortOrder) {
      // Only dispatch the action if sorting parameters change
      console.log(`Sorting ${column} in ${newSortOrder} order`);

      setSortField(column);
      setSortOrder(newSortOrder);
    }
  };

  // FILTERING
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFilter, setResetFilter] = useState(false);
  const [filterParams, setFilterParams] = useState({
    minPrice: "",
    maxPrice: "",
    minDuration: "",
    maxDuration: "",
    status: [],
    serviceGroup: [],
  });
  const handleFilterApply = (filters) => {
    setFilterParams(filters);
    setPage(1);
    setIsFilterActive(true);
    setResetFilter(false);

    localStorage.setItem("filterSettings_services", JSON.stringify(filters)); // Example for PatientList
    dispatch(
      listServices(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false,
        filters.minPrice,
        filters.maxPrice,
        filters.minDuration,
        filters.maxDuration,
        filters.status,
        filters.serviceGroup
      )
    );

    console.log("filters", filters);
  };

  // RESET FILTER
  const handleResetFilter = () => {
    const resetState = {
      employeeId: [],
      status: [],
      startDate: "",
      endDate: "",
      minPrice: "",
      maxPrice: "",
      creatorEmployeeId: [],
      coordinatorId: [],
    };
    setFilterParams(resetState);
    localStorage.setItem("filterSettings_services", JSON.stringify(resetState)); // Reset localStorage

    console.log("Resetting filters", resetState);
    console.log(
      "Resetting filters",
      localStorage.getItem("filterSettings_services")
    );
    setResetFilter(true);
    setIsFilterActive(false);
    dispatch(
      listServices(
        slug,
        debouncedSearchTerm,
        1,
        pageSize,
        sortField,
        sortOrder,
        false,
        resetState.minPrice,
        resetState.maxPrice,
        resetState.minDuration,
        resetState.maxDuration,
        resetState.status,
        resetState.serviceGroup
      )
    );
  };

  // HANDLERS

  const deleteServiceHandler = (serviceId) => {
    console.log("click");
    dispatch(deleteService(slug, serviceId));

    if (services.length === 1) {
      setPage(page - 1);
    }
  };

  // USEEFFECT
  useEffect(() => {
    if (debouncedSearchTerm.length >= 3 || debouncedSearchTerm === "") {
      setIsFetching(true);
      dispatch(
        listServices(
          slug,
          debouncedSearchTerm,
          page,
          pageSize,
          sortField,
          sortOrder,
          false,
          filterParams.minPrice,
          filterParams.maxPrice,
          filterParams.minDuration,
          filterParams.maxDuration,
          filterParams.status,
          filterParams.serviceGroup
        )
      ).then(() => setIsFetching(false));
    }
  }, [
    slug,
    debouncedSearchTerm,
    page,
    pageSize,
    sortField,
    sortOrder,
    filterParams,
    successCreate,
    successDelete,
    successUpdate,
    successUpdateStatus,
  ]);

  useEffect(() => {
    if (successCreate) {
      toastComponent.showToast(t("toasts.serviceCreate"), "success", 3000);
      closeSidePanelModal();
    } else if (errorCreate) {
      toastComponent.showToast(errorCreate, "error", 3000);
    }
    dispatch({ type: SERVICE_CREATE_RESET });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCreate, errorCreate]);

  useEffect(() => {
    if (successUpdate) {
      toastComponent.showToast(t("toasts.servicesUpdate"), "success", 3000);
      closeEditSidePanelModal();

      dispatch({ type: SERVICE_UPDATE_RESET });
    } else if (errorUpdate) {
      toastComponent.showToast(t("toasts.servicesUpdateError"), "error", 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdate, errorUpdate]);

  useEffect(() => {
    if (successDelete) {
      toastComponent.showToast(t("toasts.serviceDelete"), "success", 3000);

      dispatch({ type: SERVICE_DELETE_RESET });
    } else if (errorDelete) {
      toastComponent.showToast(t("toasts.serviceDeleteError"), "error", 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successDelete, errorDelete]);

  useEffect(() => {
    if (successUpdateStatus) {
      toastComponent.showToast(
        t("toasts.serviceUpdateStatus"),
        "success",
        3000
      );

      dispatch({ type: SERVICE_UPDATE_STATUS_RESET });
    } else if (errorUpdateStatus) {
      toastComponent.showToast(
        t("toasts.serviceUpdateStatusError"),
        "error",
        3000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdateStatus, errorUpdateStatus]);

  // SIDEPANEL HANDLERS

  const openSidePanelModal = () => {
    setSidePanelOpen(true);
    document.body.classList.add("no-scroll");
  };

  const openEditSidePanelModal = () => {
    setEditSidePanelOpen(true);

    document.body.classList.add("no-scroll");
  };

  const closeSidePanelModal = () => {
    setSidePanelOpen(false);
    setEditSidePanelOpen(false);
    setServiceId("");
    document.body.classList.remove("no-scroll");
  };
  const closeEditSidePanelModal = () => {
    setEditSidePanelOpen(false);
    setSidePanelOpen(false);
    setServiceId("");
    document.body.classList.remove("no-scroll");
  };

  const currencySymbol = useCurrencySymbol(tenant && tenant.currency);

  // CHECK PERMISSIONS

  const currentTenantSlug = slug || tenant?.slug;

  // 1. Get permissions for current tenant
  const currentTenantAccess = userInfo?.tenants?.find(
    (t) => t.slug === currentTenantSlug
  );

  const hasAccess =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.on;
  const hasAccessToCreate =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.create;
  const hasAccessToEdit =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.update;
  const hasAccessToDelete =
    currentTenantAccess?.permissions?.servicesAndServicesGroups?.delete;

  useEffect(() => {
    // Early exit if permissions are not loaded yet
    if (!currentTenantAccess?.permissions) return;

    const perms = currentTenantAccess.permissions;

    // console.log("perms", perms);

    if (perms.servicesAndServicesGroups?.on) return; // ✅ allowed, stay on dashboard
  }, [currentTenantAccess, slug]);

  if (hasAccess === false)
    return (
      <>
        <UserNoAccess pageName={t("noAccess.services")} />
      </>
    );

  // LOADING
  const isLoading = loading || isFetching;

  // LOGS

  // console.log("loading", loading);
  // console.log("isFetching", isFetching);
  // console.log("isLoading", isLoading);
  // console.log("services", services);
  // console.log("serviceId", serviceId);
  // console.log("serviceGroups", serviceGroups);

  // console.log("globalTotalAveragePrice", globalTotalAveragePrice);
  // console.log("globalTotalAverageDuration", globalTotalAverageDuration);

  // console.log("totalAveragePrice", totalAveragePrice);
  // console.log("totalAverageDuration", totalAverageDuration);
  // console.log("isInPlans", isInPlans);

  // console.log("hasAccess", hasAccess);
  // console.log("hasAccessToCreate", hasAccessToCreate);
  // console.log("hasAccessToEdit", hasAccessToEdit);
  // console.log("hasAccessToDelete", hasAccessToDelete);

  return (
    <>
      {/* HELMET  */}
      <Helmet>
        <title>{t("pageTitle.services")}</title>
        <meta name="description" content={t("pageDescription.services")} />
      </Helmet>

      {/* SIDEPANELS  START*/}
      {!isMobile ? (
        <SidePanel
          open={sidePanelModalOpen}
          close={closeSidePanelModal}
          title={t("service.createService")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            {hasAccessToCreate ? (
              <CreateService close={closeSidePanelModal} tenant={tenant} />
            ) : (
              <UserNoAccess pageName={t("noAccess.serviceCreate")} p="0" />
            )}
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={sidePanelModalOpen}
          onClose={closeSidePanelModal}
          title={t("service.createService")}
          size="full"
        >
          {hasAccessToCreate ? (
            <CreateService close={closeSidePanelModal} tenant={tenant} />
          ) : (
            <UserNoAccess pageName={t("noAccess.serviceCreate")} p="0" />
          )}
        </DrawerNew>
      )}

      {!isMobile ? (
        <SidePanel
          open={editSidePanelOpen}
          close={closeEditSidePanelModal}
          title={t("service.editService")}
          noBorder={true}
        >
          <Flex w="100%" bg={whiteMode} minW="800px" maxW="800px">
            <EditService
              hasEditAccess={hasAccessToEdit}
              close={closeEditSidePanelModal}
              serviceId={serviceId}
              tenant={tenant}
            />
          </Flex>
        </SidePanel>
      ) : (
        <DrawerNew
          isOpen={editSidePanelOpen}
          onClose={closeEditSidePanelModal}
          title={t("service.editService")}
          size="full"
        >
          <EditService
            hasEditAccess={hasAccessToEdit}
            close={closeEditSidePanelModal}
            serviceId={serviceId}
            tenant={tenant}
          />
        </DrawerNew>
      )}

      {/* SIDEPANELS  END*/}

      {/* MODALS START */}
      {showDeleteConfirmation && (
        <ConfirmDeleteModal
          hasAccessToDelete={hasAccessToDelete}
          noAccessPageName={t("noAccess.serviceDelete")}
          deleteTitle={t("service.deleteServiceTitle")}
          deleteName={serviceName}
          warningLabel={
            isInPlans
              ? t("service.deleteWarningInPlans")
              : t("service.deleteWarning")
          }
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          confirmAction={() => {
            if (isInPlans) {
              dispatch(updateServiceStatus(slug, serviceId, "inactive"));
            } else {
              deleteServiceHandler(serviceId);
            }
            setShowDeleteConfirmation(false);
          }}
        />
      )}
      {/* MODALS END */}

      {/* MAIN  */}
      <Box w="100%" minH="100%" pt="80px">
        <Flex
          direction="column"
          w="100%"
          p={["0rem 0rem 6rem", "0rem 0rem 6rem", "2rem 2rem 6rem"]}
        >
          <Box
            w="100%"
            h="100%"
            bg={whiteMode}
            overflow="hidden"
            borderRadius="2rem"
          >
            <PageHeader
              label={t("service.services")}
              buttonHandler={openSidePanelModal}
              buttonLabel={t("service.createService")}
              filter={
                <></>
                // <TreatmentPlansFilterPopover
                //   onFilterApply={handleFilterApply}
                //   isFilterActive={isFilterActive}
                //   setIsFilterActive={setIsFilterActive}
                //   filterStatusOptions="treatment"
                //   savedFiltersKey="filterSettings_treatmentPlans"
                //   resetFilter={resetFilter}
                //   onResetComplete={() => setResetFilter(false)}
                // />
              }
            />

            {/* <Box p="0 2rem">
              <ServicesStatsNew
                loading={loading}
                stats={countsByStatus}
                total={totalCount}
                averagePrice={totalAveragePrice}
                averageDuration={totalAverageDuration}
                globalTotalAverageDuration={globalTotalAverageDuration}
                globalTotalAveragePrice={globalTotalAveragePrice}
              />
            </Box> */}

            <SearchInput
              placeholder={t("service.search")}
              onChange={onChangeSearchTitle}
              searchName={searchTerm}
              p={["0.5rem 1.5rem", "0 2rem"]}
            />

            {/* {isFilterActive && (
              <ActiveFilter
                filterParams={filterParams}
                setIsFilterActive={setIsFilterActive}
                onFilterApply={handleFilterApply}
                onReset={handleResetFilter}
              />
            )} */}

            {!isMobile ? (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={services}
                      columns={columnsDesktop(
                        slug,
                        dispatch,
                        translate,
                        handleSortChange,
                        updateServiceStatus,
                        setShowDeleteConfirmation,
                        setServiceId,
                        setServiceName,
                        setIsInPlans,
                        openEditSidePanelModal,
                        currencySymbol,
                        hasAccessToEdit
                      )}
                      noItemsTitle={t("noItems.noServices")}
                      noItemsDescription={t("noItems.noServicesText")}
                      noItemsButtonLabel={t("noItems.noServicesButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(serviceId) => {
                        console.log("ONROWCLICK", serviceId);
                        setServiceId(serviceId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount && totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            ) : (
              <>
                {!isLoading ? (
                  <>
                    <TableContainerWindowed
                      loading={loading}
                      data={services}
                      columns={columnsMobile(
                        slug,
                        dispatch,
                        translate,
                        handleSortChange,
                        updateServiceStatus,
                        setShowDeleteConfirmation,
                        setServiceId,
                        setServiceName,
                        setIsInPlans,
                        openEditSidePanelModal,
                        currencySymbol,
                        hasAccessToEdit
                      )}
                      noItemsTitle={t("noItems.noServices")}
                      noItemsDescription={t("noItems.noServicesText")}
                      noItemsButtonLabel={t("noItems.noServicesButton")}
                      noItemsButtonHandler={openSidePanelModal}
                      onRowClick={(serviceId) => {
                        console.log("ONROWCLICK", serviceId);
                        setServiceId(serviceId);
                        openEditSidePanelModal();
                      }}
                      pageSize={pageSize}
                      sortField={sortField}
                      sortOrder={sortOrder}
                      handleSortChange={handleSortChange}
                    />
                    <Flex p="0rem 0" w="100%">
                      <Table.Pagination
                        loading={loading}
                        page={page}
                        pages={pages}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        navigate={navigate}
                        setPage={setPage}
                        setPageSize={setPageSize}
                        totalCount={totalCount && totalCount}
                      />
                    </Flex>
                  </>
                ) : (
                  <Flex
                    w="100%"
                    justify="center"
                    align="flex-start"
                    p="3rem 2rem"
                    minH="800px"
                  >
                    <Spinner size="lg" color={primary} />
                  </Flex>
                )}
              </>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
};

export default ServiceListNew;
